
import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Book, BookOpen, ChevronRight, Video } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface Lesson {
  id: string;
  title: string;
  description: string;
  category: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  duration: number;
  thumbnail_url?: string;
  created_at: string;
}

const Lessons = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  
  const { data: lessons, isLoading } = useQuery({
    queryKey: ['lessons', activeCategory],
    queryFn: async () => {
      let query = supabase.from('lessons').select('*');
      
      if (activeCategory) {
        query = query.eq('category', activeCategory);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching lessons:", error);
        throw error;
      }
      
      return data as Lesson[];
    },
    placeholderData: [],
  });

  const categories = [
    { id: 'startup', name: 'Startup', icon: <Book className="w-4 h-4" /> },
    { id: 'development', name: 'Development', icon: <Video className="w-4 h-4" /> },
    { id: 'design', name: 'Design', icon: <BookOpen className="w-4 h-4" /> },
  ];

  // Placeholder lessons in case the DB doesn't have any
  const placeholderLessons: Lesson[] = [
    {
      id: '1',
      title: 'Starting Your First Startup',
      description: 'Learn the essentials of launching a successful startup from ideation to execution.',
      category: 'startup',
      level: 'beginner',
      duration: 45,
      thumbnail_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978',
      created_at: new Date().toISOString(),
    },
    {
      id: '2',
      title: 'Web Development Fundamentals',
      description: 'Master the core technologies behind modern web development.',
      category: 'development',
      level: 'beginner',
      duration: 60,
      thumbnail_url: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613',
      created_at: new Date().toISOString(),
    },
    {
      id: '3',
      title: 'UI/UX Design Principles',
      description: 'Explore the key principles behind effective user interface and experience design.',
      category: 'design',
      level: 'intermediate',
      duration: 50,
      thumbnail_url: 'https://images.unsplash.com/photo-1561070791-2526d30994b5',
      created_at: new Date().toISOString(),
    },
  ];

  const displayedLessons = lessons?.length ? lessons : placeholderLessons;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="container mx-auto py-8 px-4"
    >
      <h1 className="text-4xl font-bold mb-6">
        <span className="text-white">Flux</span>{" "}
        <span className="text-flux-cyan">Lessons</span>
      </h1>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveCategory(null)} 
          className={`px-4 py-2 rounded-full text-sm ${!activeCategory 
            ? 'bg-flux-cyan text-white' 
            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
        >
          All
        </button>
        
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 ${
              activeCategory === category.id 
                ? 'bg-flux-cyan text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {category.icon}
            {category.name}
          </button>
        ))}
      </div>

      {/* Lessons List */}
      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="bg-gray-800/50 border-flux-cyan/10 h-[300px] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {displayedLessons.map(lesson => (
            <Link to={`/communities/lessons/${lesson.id}`} key={lesson.id}>
              <Card className="h-full overflow-hidden border border-flux-cyan/20 bg-flux-black/50 backdrop-blur-xl hover:border-flux-cyan/40 transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  {lesson.thumbnail_url ? (
                    <img 
                      src={lesson.thumbnail_url} 
                      alt={lesson.title} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-gray-500" />
                    </div>
                  )}
                  <div className="absolute top-0 left-0 w-full p-3">
                    <Badge className={`
                      ${lesson.level === 'beginner' ? 'bg-green-600' : ''}
                      ${lesson.level === 'intermediate' ? 'bg-yellow-600' : ''}
                      ${lesson.level === 'advanced' ? 'bg-red-600' : ''}
                    `}>
                      {lesson.level.charAt(0).toUpperCase() + lesson.level.slice(1)}
                    </Badge>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-xl font-bold text-white mb-2">{lesson.title}</h3>
                    <Badge variant="outline" className="ml-2 mt-1">
                      {lesson.duration} min
                    </Badge>
                  </div>
                  <p className="text-gray-400 mb-3 line-clamp-2">{lesson.description}</p>
                  <div className="flex justify-between items-center">
                    <Badge className="bg-gray-700 text-white">
                      {categories.find(c => c.id === lesson.category)?.name || lesson.category}
                    </Badge>
                    <ChevronRight className="w-5 h-5 text-flux-cyan" />
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default Lessons;
